import { GetSurveyRuleValueOptionsRPC } from "@pulse/pulse-rpcs/lib/survey-rules/getSurveyRuleValueOptionsRPC";
import { LeoUUID } from "@surya-digital/leo-ts-runtime";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import {
  applySnapshot,
  cast,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { SurveyRuleValueOptionModel } from "../models/SurveyRuleValueOptionModel";
import { getAPIClient } from "../../networking/APIClient";
import { useGetSurveyRuleValueOptionsRPCClientImpl } from "../rpcs/RPC";

interface GetSurveyRuleValueOptionsStoreInitialState {
  loadingGetSurveyRuleValueOptions: Record<string, boolean>;
  valueOption: Record<string, SnapshotIn<typeof SurveyRuleValueOptionModel>>;
}

const getInitialState = (): GetSurveyRuleValueOptionsStoreInitialState => ({
  loadingGetSurveyRuleValueOptions: {},
  valueOption: {},
});

const mapToSurveyRuleValueOptionModel = (
  valueOption: GetSurveyRuleValueOptionsRPC.ResponseEnums.ValueOption.ValueOption,
): SnapshotIn<typeof SurveyRuleValueOptionModel> => {
  if (
    valueOption instanceof
    GetSurveyRuleValueOptionsRPC.ResponseEnums.ValueOption.QuestionOptionCode
  ) {
    return {
      type: "QUESTION_OPTION_CODE",
      questionOptionCodes: valueOption.codes.map((code) => code.code),
    };
  }

  if (
    valueOption instanceof
    GetSurveyRuleValueOptionsRPC.ResponseEnums.ValueOption.RespondentColumnValue
  ) {
    if (
      valueOption.valueType instanceof
      GetSurveyRuleValueOptionsRPC.ResponseEnums.ValueOption
        .RespondentColumnValueEnums.ValueType.TooManyUniqueValues
    ) {
      return {
        type: "RESPONDENT_COLUMN_VALUE",
        valueType: {
          type: "TOO_MANY_UNIQUE_VALUES",
        },
      };
    }

    if (
      valueOption.valueType instanceof
      GetSurveyRuleValueOptionsRPC.ResponseEnums.ValueOption
        .RespondentColumnValueEnums.ValueType.UniqueValues
    ) {
      return {
        type: "RESPONDENT_COLUMN_VALUE",
        valueType: {
          type: "UNIQUE_VALUES",
          values: valueOption.valueType.values,
        },
      };
    }

    return valueOption.valueType satisfies never;
  }

  return valueOption satisfies never;
};

export const GetSurveyRuleValueOptionsStore = types
  .model("GetSurveyRuleValueOptionsStore", {
    loadingGetSurveyRuleValueOptions: types.map(types.boolean),
    valueOption: types.map(SurveyRuleValueOptionModel),
  })
  .actions((store) => ({
    getSurveyRuleValueOptions: flow(function* (
      surveyId: string,
      key: GetSurveyRuleValueOptionsRPC.RequestEnums.Key.Key,
      requestKey?: string,
    ) {
      const optionsKey = requestKey ?? "default";
      try {
        store.valueOption.delete(optionsKey);
        store.loadingGetSurveyRuleValueOptions.set(optionsKey, true);

        const apiClient = getAPIClient(store);
        const request = new GetSurveyRuleValueOptionsRPC.Request(
          new LeoUUID(surveyId),
          key,
        );

        const result: LeoRPCResult<
          GetSurveyRuleValueOptionsRPC.Response,
          GetSurveyRuleValueOptionsRPC.Errors.Errors
        > =
          yield useGetSurveyRuleValueOptionsRPCClientImpl(apiClient).execute(
            request,
          );

        if ("response" in result) {
          const valueOption = mapToSurveyRuleValueOptionModel(
            result.response.valueOption,
          );
          store.valueOption.set(optionsKey, cast(valueOption));
          return;
        }

        switch (result.error.code) {
          case GetSurveyRuleValueOptionsRPC.RPCError.InvalidSurveyId:
          case GetSurveyRuleValueOptionsRPC.RPCError.InvalidQuestionCode:
          case GetSurveyRuleValueOptionsRPC.RPCError.UnsupportedQuestionType:
          case GetSurveyRuleValueOptionsRPC.RPCError
            .InvalidRespondentColumnName:
            return result.error.code;
          default:
            result.error.code satisfies never;
        }
      } finally {
        store.loadingGetSurveyRuleValueOptions.delete(optionsKey);
      }
    }),

    resetStore: (): void => {
      applySnapshot(store, getInitialState());
    },
  }));

export const createGetSurveyRuleValueOptionsStore = (): Instance<
  typeof GetSurveyRuleValueOptionsStore
> => GetSurveyRuleValueOptionsStore.create(getInitialState());
